import * as Sentry from "@sentry/react";
import { INTERVIEW_MODE } from "Modules/Interview/constants";

const isProduction = process.env.REACT_APP_ENV === "production";
const isTesting = process.env.REACT_APP_ENV === "testing";
const isLocalhost = typeof window !== "undefined" &&
    ["localhost", "127.0.0.1", "0.0.0.0", "::1"].includes(
        window.location.hostname,
    );
const shouldReport = (isProduction || isTesting) && !isLocalhost;

export const TAG = {
    INVITE_ID: "invite_id",
    ERROR_TYPE: "error_type",
    INTERVIEW_MODE: "interview_mode",
};

export function inferInterviewContext(path = window.location.pathname) {
    const segments = path.split("/").filter(Boolean);

    // /kit-preview/:catcher_id/:kit_id
    if (segments[0] === "kit-preview" && segments.length >= 3) {
        return {
            mode: INTERVIEW_MODE.MOCK,
            id: segments[2], // kit_id
        };
    }

    // /video-interview/:id/:jobseekerid
    if (segments[0] === "video-interview" && segments.length === 3) {
        return {
            mode: INTERVIEW_MODE.INTEGRATION,
            id: window.atob(segments[1]), // id
        };
    }

    // /video-interview/:jobid/:jobseekerid/:id
    // /video-interview/:jobid/:jobseekerid/:id/:reInviteId
    if (segments[0] === "video-interview" && segments.length >= 4) {
        return {
            mode: INTERVIEW_MODE.STANDARD,
            id: segments[3], // id
        };
    }

    return { mode: "undefined", id: path };
}

export function captureApiCaughtError(error) {
    shouldReport
        ? Sentry.withScope((scope) => {
            const interview = inferInterviewContext();
            scope.setTag(TAG.INTERVIEW_MODE, interview.mode);
            scope.setTag(TAG.INVITE_ID, interview.id);

            scope.setTag(TAG.ERROR_TYPE, "api_caught");

            scope.captureException(error);
        })
        : console.log("[SENTRY] API Caught Error:", error);
}

export function captureResponseShapeError(error, url) {
    shouldReport
        ? Sentry.withScope((scope) => {
            const interview = inferInterviewContext();
            scope.setTag(TAG.INTERVIEW_MODE, interview.mode);
            scope.setTag(TAG.INVITE_ID, interview.id);

            scope.setTag(TAG.ERROR_TYPE, "api_shape");
            if (url) scope.setTag("api_url", url);

            scope.captureException(error);
        })
        : console.log("[SENTRY] Response Shape Error:", error);
}

export function captureApiTransportError(error, url) {
    shouldReport
        ? Sentry.withScope((scope) => {
            const interview = inferInterviewContext();
            scope.setTag(TAG.INTERVIEW_MODE, interview.mode);
            scope.setTag(TAG.INVITE_ID, interview.id);

            scope.setTag(TAG.ERROR_TYPE, "api_transport");
            if (url) scope.setTag("api_url", url);

            scope.setContext("api", {
                url: url,
                method: error.config?.method,
                status: error?.status,
                serverMessage: error?.data?.message,
            });
            scope.captureException(error);
        })
        : console.log("[SENTRY] API Transport Error:", error);
}

// Request never got an HTTP response (offline, blocked by extension/firewall, CORS, DNS, timeout).
// Reported as a warning via captureApiNetworkWarning, so it is dropped in beforeSend
// when it reaches an error boundary.
export class ApiNetworkError extends Error {
    constructor(message, code) {
        super(message);
        this.name = "ApiNetworkError";
        this.code = code;
    }
}

export function captureApiNetworkWarning(error, context = {}) {
    shouldReport
        ? Sentry.withScope((scope) => {
            const interview = inferInterviewContext();
            scope.setTag(TAG.INTERVIEW_MODE, interview.mode);
            scope.setTag(TAG.INVITE_ID, interview.id);

            scope.setTag(TAG.ERROR_TYPE, "api_network");
            scope.setTag("error_code", error?.code);
            scope.setTag("navigator_online", String(navigator.onLine));

            scope.setContext("api", {
                ...context,
                code: error?.code,
                message: error?.data?.message,
            });
            scope.setLevel("warning");
            scope.captureMessage(context.label || "API network error");
        })
        : console.log("[SENTRY] API Network Warning:", error, context);
}

export function sentryRootOptions() {
    return shouldReport
        ? {
            // Callback called when an error is thrown and not caught by an ErrorBoundary.
            onUncaughtError: Sentry.reactErrorHandler(),
            // Callback called when React catches an error in an ErrorBoundary.
            onCaughtError: Sentry.reactErrorHandler(),
            // Callback called when React automatically recovers from errors.
            onRecoverableError: Sentry.reactErrorHandler(),
        }
    : undefined;
}

export function sentryInit() {
    shouldReport &&
      Sentry.init({
          dsn: process.env.REACT_APP_SENTRY_DSN,
          environment: process.env.REACT_APP_ENV,

          integrations: [
            Sentry.browserTracingIntegration(),

            Sentry.replayIntegration({
                networkDetailAllowUrls: [
                    /^https:\/\/interviewfastapi\.jobma\.com/,
                ],
                networkRequestHeaders: ["X-Custom-Header"],
                networkResponseHeaders: ["X-Custom-Header"],
            }),
        ],

          // Set tracesSampleRate to 1.0 to capture 100%
          // of transactions for tracing.
          // Learn more at
          // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
          tracesSampleRate: isProduction ? 1 : 0.1,
          // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
          tracePropagationTargets: [
              process.env.REACT_APP_URL,
              process.env.REACT_APP_SENTRY_URL,
          ],

          // Capture Replay for 10% of all sessions,
          // plus for 100% of sessions with an error
          // Learn more at
          // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
          replaysSessionSampleRate: isProduction ? 0.1 : 0,
          replaysOnErrorSampleRate: isProduction ? 1 : 0,

          beforeSend(event, hint) {
              const error = hint?.originalException;

              const isAbortError =
                error?.name === "AbortError" ||
                error?.message?.includes("Fetch is aborted") ||
                error?.message?.includes("The operation was aborted") ||
                error?.message?.includes("signal is aborted");

              if (isAbortError) {
                  return null;
              }

              // Already reported as a warning by captureApiNetworkWarning
              if (error instanceof ApiNetworkError) {
                  return null;
              }

                return event;
            },
        });

    window.addEventListener("unhandledrejection", (event) => { //to track the unhandledrejection is coming from
        console.error("Unhandled Rejection:", event.reason);

        Sentry.withScope((scope) => {
            const interview = inferInterviewContext();

            scope.setTag(TAG.INTERVIEW_MODE, interview.mode);
            scope.setTag(TAG.INVITE_ID, interview.id);
            scope.setTag(TAG.ERROR_TYPE, "unhandled_rejection");

            scope.setExtra("reason", event.reason);

            if (event.reason instanceof Error) {
                Sentry.captureException(event.reason);
            } else {
                Sentry.captureException(
                    new Error(
                        typeof event.reason === "string"
                            ? event.reason
                            : JSON.stringify(event.reason),
                    ),
                );
            }
        });
    });
}
